import React from 'react';
import { FaChevronDown, FaChevronUp, FaSignInAlt, FaEnvelope } from 'react-icons/fa';
import { useErrorPage } from './useErrorPage';
import { ErrorPageProps, ErrorPageBoundaryProps, ErrorPageBoundaryState, ErrorIconType } from './types';
import styles from './ErrorPage.module.css';
import logger from '../../utils/logger';

// SVGアイコンコンポーネント
export const ErrorIcon404: React.FC = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
  </svg>
);

export const ErrorIcon500: React.FC = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 16h2v-2h-2v2zm1-4c-.55 0-1-.45-1-1V7c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1z"/>
  </svg>
);

// エラーアイコンコンポーネントマップ
const ERROR_ICONS: Record<ErrorIconType, React.ReactElement> = {
  ERROR_404: <ErrorIcon404 />,
  ERROR_500: <ErrorIcon500 />
};

// 最小限のフォールバックUI（エラーページ自体がエラーになった場合）
export const FallbackErrorUI: React.FC = () => (
  <div className={styles['fallback-error']}>
    <h2>予期せぬエラーが発生しました</h2>
    <p>申し訳ありませんが、エラーの表示中に問題が発生しました。</p>
    <button
      onClick={() => window.location.href = '/login'}
    >
      ログイン画面へ
    </button>
  </div>
);

// エラーキャッチ用コンポーネント
export class ErrorPageBoundary extends React.Component<ErrorPageBoundaryProps, ErrorPageBoundaryState> {
  state: ErrorPageBoundaryState = { hasError: false, error: null };
  
  static getDerivedStateFromError(error: Error): ErrorPageBoundaryState {
    return { hasError: true, error };
  }
  
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // エラー情報をstring型に変換してloggerに渡す
    const errorMessage = `コンポーネントエラー：${error.message}, コンポーネントスタック：${errorInfo.componentStack}`;
    
    // nullを渡して型エラーを回避
  }
  
  render(): React.ReactNode {
    if (this.state.hasError) {
      return <FallbackErrorUI />;
    }
    return this.props.children;
  }
}

const ErrorPage: React.FC<ErrorPageProps> = () => {
  const {
    errorInfo,
    showTrace,
    toggleTrace,
    handleGoToLogin,
    handleContactSupport,
    formatTimestamp,
    getErrorStatusText,
    renderErrorIcon
  } = useErrorPage();

  // エラーアイコンを表示
  const displayErrorIcon = ERROR_ICONS[renderErrorIcon()];

  return (
    <ErrorPageBoundary>
      <div className={styles['error-page-container']}>
        <div className={styles['error-content']}>
          <div className={styles['brand-logo']}>
            <img
              src="/asset/logo.svg"
              alt="MedTube"
              data-testid="error-page__logo"
            />
          </div>
          
          <div className={styles['error-icon']} data-testid="error-page__icon">
            {displayErrorIcon}
          </div>
          <h1 className={styles['error-title']}>エラーが発生しました</h1>
          <p className={styles['error-message']} data-testid="error-page__message">{errorInfo.message}</p>
          
          <div className={styles['error-details']}>
            <div className={styles['error-meta-item']}>
              <span className={styles['error-meta-label']}>ステータス:</span>
              <span className={styles['error-meta-value']} data-testid="error-page__status">
                {errorInfo.status} {getErrorStatusText(errorInfo.status)}
              </span>
            </div>
            <div className={styles['error-meta-item']}>
              <span className={styles['error-meta-label']}>発生時刻:</span>
              <span className={styles['error-meta-value']}>
                {formatTimestamp(errorInfo.timestamp)}
              </span>
            </div>
            <div className={styles['error-meta-item']}>
              <span className={styles['error-meta-label']}>URL:</span>
              <span className={styles['error-meta-value']}>
                {errorInfo.path || window.location.pathname}
              </span>
            </div>
            
            {errorInfo.stack && (
              <div className={styles['trace-container']}>
                <button 
                  className={styles['trace-toggle']} 
                  onClick={toggleTrace}
                  data-testid="error-page__trace-toggle"
                >
                  エラー詳細情報
                  {showTrace ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {showTrace && (
                  <pre className={styles['trace-content']} data-testid="error-page__trace-content">
                    {errorInfo.stack}
                  </pre>
                )}
              </div>
            )}
          </div>
          
          <div className={styles['action-buttons']}>
            <button 
              className={styles['action-button']} 
              onClick={handleGoToLogin}
              data-testid="error-page__login-button"
            >
              <FaSignInAlt /> ログイン画面へ
            </button>
            <button 
              className={`${styles['action-button']} ${styles['action-button-secondary']}`} 
              onClick={handleContactSupport}
              data-testid="error-page__support-button"
            >
              <FaEnvelope /> サポートに問い合わせる
            </button>
          </div>
          
          <div className={styles['support-email']}>
            問題が解決しない場合は、MedTubeサポートチームまでご連絡ください：<br />
            <a href="mailto:surgilink.medtube@gmail.com">surgilink.medtube@gmail.com</a>
          </div>
        </div>
      </div>
    </ErrorPageBoundary>
  );
};

export default ErrorPage; 